// Web Push (VAPID) helpers. Deliberately minimal and reusable: subscribe,
// unsubscribe and a small "is it a good moment to ask?" gate.

import { api } from "./api";

const DISMISS_KEY = "spotdrop_push_dismissed";
const SESSIONS_KEY = "spotdrop_sessions";
const SESSION_FLAG = "spotdrop_session_counted";
const ENGAGED_KEY = "spotdrop_engaged";

const read = (k) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const write = (k, v) => {
  try {
    localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
};

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export function permissionState() {
  return pushSupported() ? Notification.permission : "unsupported";
}

/** Counts one visit per browser session (used to ask on the 2nd session). */
export function bumpSession() {
  try {
    if (sessionStorage.getItem(SESSION_FLAG)) return;
    sessionStorage.setItem(SESSION_FLAG, "1");
  } catch {
    /* ignore */
  }
  write(SESSIONS_KEY, String(Number(read(SESSIONS_KEY) || 0) + 1));
}

/** Called after a real action (drop / claim / saw a spot). */
export function markEngaged() {
  write(ENGAGED_KEY, "1");
}

export function dismissPrompt() {
  write(DISMISS_KEY, "1");
}

/** Never on first load, never again after "Not now". */
export function shouldPrompt() {
  if (!pushSupported() || Notification.permission !== "default") return false;
  if (read(DISMISS_KEY) === "1") return false;
  return read(ENGAGED_KEY) === "1" || Number(read(SESSIONS_KEY) || 0) >= 2;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function getSubscription() {
  if (!pushSupported()) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Asks permission (must be called from a user gesture) and stores the subscription. */
/** Make sure this device really has a push subscription on the server.
 *  Permission "granted" does NOT mean subscribed: a driver who allowed
 *  notifications in the past can still have no subscription, in which case the
 *  server has nowhere to send a reminder. Never prompts when already granted. */
export async function ensurePushSubscription() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  if (Notification.permission === "denied") return { ok: false, reason: "denied" };
  if (Notification.permission === "default") {
    const permission = await Notification.requestPermission();
    if (permission !== "granted") return { ok: false, reason: "denied" };
  }
  try {
    const { data } = await api.get("/push/config");
    if (!data?.push_enabled || !data?.public_key) return { ok: false, reason: "not_configured" };
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.public_key),
      });
    }
    // Re-posting an existing subscription is idempotent (unique on endpoint).
    await api.post("/push/subscribe", sub.toJSON());
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "subscribe_failed" };
  }
}

/** Driver-triggered check: did a real notification reach this device? */
export async function sendTestPush() {
  const ensured = await ensurePushSubscription();
  if (!ensured.ok) return ensured;
  try {
    const { data } = await api.post("/push/test");
    return { ok: !!data?.ok, reason: data?.reason, sent: data?.sent, subscriptions: data?.subscriptions };
  } catch {
    return { ok: false, reason: "request_failed" };
  }
}

export async function enablePush() {
  if (!pushSupported()) throw new Error("unsupported");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") throw new Error("denied");

  const { data } = await api.get("/push/config");
  if (!data?.push_enabled || !data?.public_key) throw new Error("not_configured");

  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return true;
}

export async function disablePush() {
  const sub = await getSubscription();
  if (!sub) return true;
  const endpoint = sub.endpoint;
  try {
    await sub.unsubscribe();
  } catch {
    /* ignore */
  }
  await api.post("/push/unsubscribe", { endpoint }).catch(() => {});
  return true;
}
